/**
 * The health probes (PLAN §10.2's status path, §11.1): what `GET /health`
 * answers on the cloud API and on a node. Unauthenticated, cheap, and NOT in
 * the route table — they are not tenant data, they answer before a caller
 * could have a token (the connectivity panel probes them before and during
 * sign-in), and the cloud serves `/health` on its own unauthenticated API
 * Gateway route (`infrastructure/api-http.tf`) in front of the router.
 *
 *     cloud:  { service: 'cloud-api', stage, version, time }
 *     node:   { service: 'state-service', status, stage, version, time,
 *               tenantId, siteId, nodeId, label, storeId, schemaVersion,
 *               cloudLink, outboxDepth }
 *
 * `version` is what is running: the cloud API's bundle hash (set by OpenTofu
 * from the Lambda zip), a node's package hash (D15; `ACP_VERSION`, `dev`
 * until packaging lands). `time` is the server's clock (ISO), so a reader can
 * show drift.
 *
 * `cloudLink` is the node's own view of its site link to the cloud (PLAN
 * §10.2's "Node→cloud" column): `not-configured` until the site link exists
 * (phase 4), then `connected` / `disconnected`. `outboxDepth` is how many
 * applied patches wait in the node's outbox for that link.
 *
 * The producers answer these strict schemas (their tests parse what they
 * answer). The READERS — the CLI's `acp status`, the SPA's probes — parse
 * `anyHealthWireSchema`, built from the `.loose()` variants: they talk to servers
 * of other versions (PLAN §4.3), and a newer server adding a field must not
 * read as unreachable.
 */
import { z } from 'zod';
import { pairNodeRoleWireSchema, pairRoleWireSchema, witnessInForceWireSchema, witnessKindWireSchema } from './witness.js';
import { boundedIdWireSchema, cloudLinkStateReadWireSchema, cloudLinkStateWireSchema, isoTimestampWireSchema, peerLinkStateWireSchema } from './primitives.js';

export const cloudHealthWireSchema = z
  .object({
    service: z.literal('cloud-api'),
    stage: z.string().min(1),
    version: z.string().min(1),
    time: isoTimestampWireSchema,
  })
  .strict();

/**
 * Something about the node's cloud identity an operator must act on: today only `revoked` — the cloud replaced or
 * removed this node, so its site link is stopped (docs/sitelink.md). `message` says what to do.
 */
export const cloudIdentityNoticeWireSchema = z
  .object({
    status: z.literal('revoked'),
    since: isoTimestampWireSchema,
    message: z.string().min(1),
  })
  .strict();
export type CloudIdentityNoticeWire = z.infer<typeof cloudIdentityNoticeWireSchema>;

/**
 * A node's main/backup pair as it sees it (PLAN §8.6: the connectivity panel
 * "shows which node is active per production"): the peer link, the
 * witnesses, and each production's role here. Absent on a node without a peer.
 */
export const nodeRedundancyHealthWireSchema = z
  .object({
    /** D21's pairing: main or backup (roles are per production; this is the node's place in the site). */
    nodeRole: pairNodeRoleWireSchema,
    peer: z
      .object({
        nodeId: boundedIdWireSchema,
        url: z.string().min(1),
        /** The peer link (its long poll answered within the lease window). */
        link: peerLinkStateWireSchema,
        lastContactAgoMs: z.number().int().min(0).nullable(),
      })
      .strict(),
    witnesses: z.array(
      z
        .object({ url: z.string().min(1), kind: witnessKindWireSchema, reachable: z.boolean(), lastContactAgoMs: z.number().int().min(0).nullable() })
        .strict(),
    ),
    /** Which witness confirms failovers and renews the lease here, and the expected failover time (wave 4c). */
    witness: witnessInForceWireSchema.optional(),
    productions: z.array(
      z
        .object({
          productionId: boundedIdWireSchema,
          /** This node's process: active, standby, or none running here. */
          role: pairRoleWireSchema.nullable(),
          term: z.number().int().min(0),
          activeNode: boundedIdWireSchema.nullable(),
          alarm: z.string().nullable(),
        })
        .strict(),
    ),
  })
  .strict();
export type NodeRedundancyHealthWire = z.infer<typeof nodeRedundancyHealthWireSchema>;

export const nodeHealthWireSchema = z
  .object({
    service: z.literal('state-service'),
    /** Liveness, kept from the first shape of this endpoint. */
    status: z.literal('ok'),
    /** The cloud stage this node belongs to (`ACP_STAGE`), or `local` for a node with none configured. */
    stage: z.string().min(1),
    version: z.string().min(1),
    time: isoTimestampWireSchema,
    tenantId: boundedIdWireSchema,
    siteId: boundedIdWireSchema,
    nodeId: boundedIdWireSchema,
    /** What operators call this node (`ACP_NODE_LABEL`, default the node id). */
    label: z.string().min(1),
    storeId: z.string().min(1),
    schemaVersion: z.number().int(),
    cloudLink: cloudLinkStateWireSchema,
    outboxDepth: z.number().int().min(0),
    /** The main/backup pair (phase 5), on a node with a peer. */
    redundancy: nodeRedundancyHealthWireSchema.optional(),
    /** Set when the cloud revoked this node (its link is stopped): what happened and what to do. */
    cloudIdentity: cloudIdentityNoticeWireSchema.optional(),
    /** Set when the node's LAN IP is one its peer cannot reach (`lanIpProblem`, `sites.ts`): why, and the ways out. */
    lanWarning: z.string().min(1).optional(),
  })
  .strict();

/**
 * What a reader accepts: either service, unknown extra fields kept, and the
 * closed values opened up — a newer node reporting `status: 'degraded'` or a
 * cloud link state this reader does not know must read as REACHABLE, not as
 * "not an ACP health report" (which would fail a working primary over). A
 * link state it does not know is, as far as it can tell, not connected.
 */
export const anyHealthWireSchema = z.discriminatedUnion('service', [
  cloudHealthWireSchema.loose(),
  // A notice this reader cannot read is dropped, not a reason to call the node unreachable.
  nodeHealthWireSchema.extend({ status: z.string().min(1), cloudLink: cloudLinkStateReadWireSchema, cloudIdentity: cloudIdentityNoticeWireSchema.optional().catch(undefined) }).loose(),
]);

export type CloudHealthWire = z.infer<typeof cloudHealthWireSchema>;
export type NodeHealthWire = z.infer<typeof nodeHealthWireSchema>;
export type AnyHealthWire = z.infer<typeof anyHealthWireSchema>;

/** Where every host answers its health probe, relative to its API base URL. */
export const HEALTH_PATH = '/health';
